import { useEffect, useState } from 'react';
import Cohort, { CohortStatus, LiveSession } from '@/models/cohort';
import {
    useCohort,
    useCreateCohort,
    useUpdateCohort,
    useDeleteCohort,
    CohortMutationPayload,
    CohortMember,
    addCohortMember,
} from '@/services/cohorts';
import FormModal from '@/shared/components/form-modal';
import { MaterialFormContainer, MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { useLiveSessions } from '@/services/live-sessions';
import CohortMembers from './cohort-members';
import { IANA_TIME_ZONES, isValidIanaTimeZone } from '@/utils/timezones';
import { darkDangerText } from '@/shared/dark-inline-styles';

interface CohortModalProps {
    isOpen: boolean;
    selectedCohortId?: string;
    toggle: () => void;
}

// The 33 course is the only course these cohorts run, so courseId is fixed and
// hidden from the form — but it MUST still be sent in the payload.
const DEFAULT_COURSE_ID = '33';

// A live-session row as edited in the form. `scheduledAt` is held as a
// datetime-local string (`YYYY-MM-DDTHH:mm`) and converted to ISO on save.
interface LiveSessionForm {
    scheduledAt: string;
    /**
     * One of OUR live rooms. This and `externalLink` are the two possible
     * destinations, and whichever is set is where the session happens — there is
     * deliberately no third field naming the provider.
     */
    liveSessionId: string;
    externalLink: string;
    durationMinutes: number | '';
    title: string;
    dayNumber: number | '';
    recordingUrl: string;
    // Server-managed by the Google Meet integration — read-only, echoed back so an
    // edit doesn't drop them.
    meetSpaceId: string;
    transcriptUrl: string;
    attendeeCount: number | null;
}

interface CohortForm {
    title: string;
    description: string;
    instructorId: string;
    startDate: string;
    endDate: string;
    joinFormUrl: string;
    timezone: string;
    attendeeCapacity: number | '';
    status: CohortStatus;
    liveSessions: LiveSessionForm[];
}

const emptyForm = (): CohortForm => ({
    title: '',
    description: '',
    instructorId: '',
    startDate: '',
    endDate: '',
    joinFormUrl: '',
    timezone: '',
    attendeeCapacity: '',
    status: 'draft',
    liveSessions: [],
});

const emptyLiveSession = (): LiveSessionForm => ({
    scheduledAt: '',
    liveSessionId: '',
    externalLink: '',
    durationMinutes: '',
    title: '',
    dayNumber: '',
    recordingUrl: '',
    meetSpaceId: '',
    transcriptUrl: '',
    attendeeCount: null,
});

// The offset (ms, zone − UTC) of `timeZone` at a given instant. Reads the
// instant's wall-clock components in the zone via Intl, treats them as UTC and
// diffs — the standard DST-correct offset with no external dependency.
const zoneOffsetMs = (date: Date, timeZone: string): number => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(date);
    const p: Record<string, number> = {};
    for (const part of parts) if (part.type !== 'literal') p[part.type] = Number(part.value);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - date.getTime();
};

// An ISO (UTC) timestamp → the `YYYY-MM-DDTHH:mm` a datetime-local input wants,
// read in the COHORT's timezone — so the admin sees + edits the cohort's own
// wall-clock (e.g. "15:30" for a Dubai cohort), NOT the browser's local time.
// Falls back to browser-local for a legacy/invalid zone (non-breaking).
const isoToLocalInput = (iso: string | undefined, timeZone: string): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    if (!isValidIanaTimeZone(timeZone)) {
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).formatToParts(d);
    const g = (t: string) => parts.find((x) => x.type === t)?.value ?? '';
    return `${g('year')}-${g('month')}-${g('day')}T${g('hour')}:${g('minute')}`;
};

// A datetime-local value — a wall-clock the admin typed IN the cohort's timezone
// — → a UTC ISO string. So "15:30" for a Dubai cohort is stored as the instant
// that reads 15:30 in Dubai. Attendees see that instant converted to THEIR OWN
// local time (the app/site render with no fixed zone) — not 15:30-for-everyone.
// Falls back to browser-local for a legacy/invalid zone. Returns '' for anything
// unparseable.
const localInputToIso = (value: string, timeZone: string): string => {
    if (!value) return '';
    if (!isValidIanaTimeZone(timeZone)) {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? '' : d.toISOString();
    }
    const approx = new Date(`${value}:00.000Z`);
    if (Number.isNaN(approx.getTime())) return '';
    // Two passes so a value near a DST transition resolves to the right instant.
    let instant = new Date(approx.getTime() - zoneOffsetMs(approx, timeZone));
    instant = new Date(approx.getTime() - zoneOffsetMs(instant, timeZone));
    return instant.toISOString();
};

const Hint = ({ children }: { children: React.ReactNode }) => (
    <p style={{ margin: '-6px 0 10px', fontSize: 12, color: '#999999', lineHeight: 1.5 }}>{children}</p>
);

const CohortModal = ({ isOpen, toggle, selectedCohortId }: CohortModalProps) => {
    const { data: cohort, isLoading } = useCohort(selectedCohortId as string, isOpen);
    const { mutateAsync: createCohort, isPending: isCreating } = useCreateCohort();
    const { mutateAsync: updateCohort, isPending: isUpdating } = useUpdateCohort();
    const { mutateAsync: deleteCohort, isPending: isDeleting } = useDeleteCohort();

    const [form, setForm] = useState<CohortForm>(emptyForm());
    // Our own rooms, for the destination picker below.
    const { data: liveSessionsResponse } = useLiveSessions();
    const [currentAttendeeCount, setCurrentAttendeeCount] = useState(0);
    const [error, setError] = useState<string | null>(null);
    // Members chosen while CREATING a cohort (no id yet) — committed after the
    // cohort is created. In edit mode CohortMembers hits the API immediately instead.
    const [stagedMembers, setStagedMembers] = useState<CohortMember[]>([]);
    // Spans the ENTIRE save (create + the staged-member commit), so the modal stays
    // disabled through the whole op — the mutation `isPending` flags flip false as
    // soon as create resolves, before the member commit runs.
    const [isSaving, setIsSaving] = useState(false);
    // The id of a cohort just created IN THIS session. Once set, a subsequent Save
    // is an UPDATE (never a second create) — prevents duplicate cohorts if the modal
    // stays open (e.g. after a member-add failure) and Save is clicked again.
    const [createdCohortId, setCreatedCohortId] = useState<string | null>(null);

    // `isOpen` in the deps so re-opening the SAME cohort re-fills the form from
    // the cached data (the close-reset below would otherwise leave it blank).
    useEffect(() => {
        if (!isOpen) return;
        if (cohort?.data) {
            const c: Cohort = cohort.data;
            setForm({
                title: c.title,
                description: c.description ?? '',
                instructorId: c.instructorId ?? '',
                startDate: c.startDate ?? '',
                endDate: c.endDate ?? '',
                joinFormUrl: c.joinFormUrl ?? '',
                timezone: c.timezone,
                attendeeCapacity: typeof c.attendeeCapacity === 'number' ? c.attendeeCapacity : '',
                status: c.status,
                liveSessions: (c.liveSessions ?? []).map((s) => ({
                    scheduledAt: isoToLocalInput(s.scheduledAt, c.timezone),
                    liveSessionId: s.liveSessionId ?? '',
                    externalLink: s.externalLink ?? '',
                    durationMinutes: typeof s.durationMinutes === 'number' ? s.durationMinutes : '',
                    title: s.title ?? '',
                    dayNumber: typeof s.dayNumber === 'number' ? s.dayNumber : '',
                    recordingUrl: s.recordingUrl ?? '',
                    meetSpaceId: s.meetSpaceId ?? '',
                    transcriptUrl: s.transcriptUrl ?? '',
                    attendeeCount: typeof s.attendeeCount === 'number' ? s.attendeeCount : null,
                })),
            });
            setCurrentAttendeeCount(c.currentAttendeeCount ?? 0);
        } else if (!selectedCohortId) {
            setForm(emptyForm());
            setCurrentAttendeeCount(0);
        }
        setError(null);
    }, [isOpen, cohort?.data, selectedCohortId]);

    useEffect(() => {
        if (!isOpen) {
            setForm(emptyForm());
            setCurrentAttendeeCount(0);
            setError(null);
            setStagedMembers([]);
            setIsSaving(false);
            setCreatedCohortId(null);
        }
    }, [isOpen]);

    const set = <K extends keyof CohortForm>(key: K, value: CohortForm[K]) => setForm((f) => ({ ...f, [key]: value }));

    const setSession = <K extends keyof LiveSessionForm>(index: number, key: K, value: LiveSessionForm[K]) =>
        setForm((f) => ({
            ...f,
            liveSessions: f.liveSessions.map((s, i) => (i === index ? { ...s, [key]: value } : s)),
        }));

    const addSession = () => setForm((f) => ({ ...f, liveSessions: [...f.liveSessions, emptyLiveSession()] }));

    const removeSession = (index: number) => setForm((f) => ({ ...f, liveSessions: f.liveSessions.filter((_, i) => i !== index) }));

    const validate = (): string | null => {
        if (!form.title.trim()) return 'Title is required';
        // Server requires startDate (ISO8601); validate client-side for a friendly message
        // instead of a raw "startDate must be an ISO date" 400.
        if (!form.startDate) return 'Start date is required';
        if (!form.timezone.trim()) return 'Timezone is required';
        if (!isValidIanaTimeZone(form.timezone)) return 'Please select a valid IANA time zone';
        if (form.attendeeCapacity === '' || Number(form.attendeeCapacity) < 1) return 'Attendee capacity must be at least 1';
        // Capacity below the current headcount would silently block ALL new enrollments
        // (the enroll transaction throws cohortFull). Reject it with a clear message.
        // (attendeeCapacity is a number here — the check above returned on '').
        if (Number(form.attendeeCapacity) < currentAttendeeCount)
            return `Attendee capacity can’t be below the current attendee count (${currentAttendeeCount}).`;
        for (let i = 0; i < form.liveSessions.length; i++) {
            const s = form.liveSessions[i];
            const n = i + 1;
            if (!s.scheduledAt) return `Live session ${n}: scheduled time is required`;
            // EITHER destination satisfies the server's `assertLiveSessionLinks`: one
            // of our own rooms, or a pasted Meet link. Requiring the link alone would
            // make every in-app session unsavable.
            if (!s.liveSessionId && !s.externalLink.trim()) {
                return `Live session ${n}: pick a Harmonea live session, or paste a meeting link`;
            }
            if (s.durationMinutes === '' || Number(s.durationMinutes) <= 0) return `Live session ${n}: duration (minutes) is required`;
        }
        return null;
    };

    const buildPayload = (): CohortMutationPayload => {
        const liveSessions: LiveSession[] = form.liveSessions.map((s) => {
            const session: LiveSession = {
                scheduledAt: localInputToIso(s.scheduledAt, form.timezone),
                externalLink: s.externalLink.trim(),
                durationMinutes: Number(s.durationMinutes),
            };
            if (s.liveSessionId) session.liveSessionId = s.liveSessionId;
            if (s.title.trim()) session.title = s.title.trim();
            if (s.dayNumber !== '') session.dayNumber = Number(s.dayNumber);
            if (s.recordingUrl.trim()) session.recordingUrl = s.recordingUrl.trim();
            // Echo back the server-managed Meet fields so an edit doesn't drop them.
            if (s.meetSpaceId.trim()) session.meetSpaceId = s.meetSpaceId.trim();
            if (s.transcriptUrl.trim()) session.transcriptUrl = s.transcriptUrl.trim();
            if (s.attendeeCount != null) session.attendeeCount = s.attendeeCount;
            return session;
        });

        const payload: CohortMutationPayload = {
            courseId: DEFAULT_COURSE_ID,
            title: form.title.trim(),
            startDate: form.startDate,
            timezone: form.timezone.trim(),
            attendeeCapacity: Number(form.attendeeCapacity),
            status: form.status,
            liveSessions,
        };
        if (form.description.trim()) payload.description = form.description.trim();
        if (form.instructorId.trim()) payload.instructorId = form.instructorId.trim();
        if (form.endDate) payload.endDate = form.endDate;
        if (form.joinFormUrl.trim()) payload.joinFormUrl = form.joinFormUrl.trim();
        return payload;
    };

    const handleSave = async () => {
        if (isSaving) return; // guard against a double-submit during the member-commit phase
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }
        setError(null);
        setIsSaving(true);
        try {
            const payload = buildPayload();
            // A cohort already created in this session (createdCohortId) or being
            // edited (selectedCohortId) UPDATES — never creates a second cohort.
            const existingId = selectedCohortId ?? createdCohortId;
            // callApi never throws on API errors — inspect result.status.
            const result = existingId ? await updateCohort({ cohortId: existingId, ...payload }) : await createCohort(payload);
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to save the cohort.');
                return;
            }
            // On the FIRST create only, commit the members staged in the form now that
            // we have an id (each add enrols them + grants Course 33). In edit mode
            // CohortMembers already committed each add/remove immediately.
            if (!existingId) {
                const newCohortId = result.data?.id;
                if (newCohortId) {
                    setCreatedCohortId(newCohortId); // a re-click now updates, not re-creates
                    if (stagedMembers.length) {
                        const outcomes = await Promise.all(stagedMembers.map((m) => addCohortMember(newCohortId, m.userId)));
                        const failed = outcomes.filter((o) => o.status === 'error').length;
                        if (failed) {
                            // Cohort is saved; the members section below is now live (keyed on
                            // the new id) so the admin can retry the failed adds in place.
                            setError(`Cohort saved, but ${failed} member(s) couldn’t be added — add them in the Members list below.`);
                            return;
                        }
                    }
                }
            }
            toggle();
        } catch (e) {
            console.error('Failed to save cohort:', e);
            setError('Failed to save the cohort. Check the console for details.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        const deleteId = selectedCohortId ?? createdCohortId;
        if (!deleteId) return;
        try {
            const result = await deleteCohort({ cohortId: deleteId });
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to delete the cohort.');
                return;
            }
            toggle();
        } catch (e) {
            console.error('Failed to delete cohort:', e);
            setError('Failed to delete the cohort. Check the console for details.');
        }
    };

    // A stored zone that isn't in the picker (e.g. a legacy free-text typo) — the
    // select surfaces it flagged so the admin re-picks a valid one.
    const timezoneInvalid = !!form.timezone && !IANA_TIME_ZONES.includes(form.timezone);

    return (
        <FormModal
            isOpen={isOpen}
            showDelete={!!(selectedCohortId ?? createdCohortId)}
            title={`${(selectedCohortId ?? createdCohortId) ? 'Edit' : 'New'} Cohort`}
            body={
                <MaterialFormContainer onSubmit={handleSave}>
                    {error && (
                        <div
                            style={{
                                background: 'rgba(255,107,107,0.12)',
                                color: '#ff8a8a',
                                border: '1px solid rgba(255,107,107,0.35)',
                                padding: 10,
                                borderRadius: 4,
                                marginBottom: 12,
                            }}
                        >
                            {error}
                        </div>
                    )}

                    <MaterialTextField label="Title" value={form.title} onChange={(e) => set('title', e.target.value)} />

                    <MaterialTextField label="Description" multiline value={form.description} onChange={(e) => set('description', e.target.value)} />

                    <MaterialTextField
                        label="Instructor ID (optional)"
                        value={form.instructorId}
                        onChange={(e) => set('instructorId', e.target.value)}
                    />

                    {/* ── Schedule ─────────────────────────────────────────── */}
                    <p style={{ margin: '18px 0 4px', fontWeight: 600 }}>Schedule</p>
                    <div style={{ display: 'flex', gap: 16 }}>
                        <MaterialTextField
                            label="Start date"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={form.startDate}
                            onChange={(e) => set('startDate', e.target.value)}
                        />
                        <MaterialTextField
                            label="End date (optional)"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={form.endDate}
                            onChange={(e) => set('endDate', e.target.value)}
                        />
                    </div>
                    <MaterialTextField
                        name="select"
                        select
                        label="Timezone"
                        value={form.timezone}
                        error={timezoneInvalid}
                        onChange={(e) => set('timezone', e.target.value)}
                    >
                        {/* An existing cohort may carry a zone that predates this select (e.g. a
                            legacy free-text typo). Surface it — selected + flagged — so the admin
                            sees the bad value and is nudged to pick a real zone, rather than the
                            field silently blanking. */}
                        {timezoneInvalid && (
                            <MaterialFormOption value={form.timezone}>{form.timezone} — invalid, please re-select</MaterialFormOption>
                        )}
                        {IANA_TIME_ZONES.map((tz) => (
                            <MaterialFormOption key={tz} value={tz}>
                                {tz}
                            </MaterialFormOption>
                        ))}
                    </MaterialTextField>
                    <Hint>
                        {timezoneInvalid
                            ? 'This is not a valid IANA time zone — please re-select. You enter session times in this zone; attendees see them in their own local time.'
                            : 'IANA time zone you enter the session wall-clock times in. Attendees see each session converted to their own local time.'}
                    </Hint>

                    <MaterialTextField
                        label="Request-to-join form URL (optional)"
                        type="url"
                        value={form.joinFormUrl}
                        onChange={(e) => set('joinFormUrl', e.target.value)}
                    />
                    <Hint>The Google Form (or any link) shown as “Request to join” on the marketing site for this cohort when it’s upcoming.</Hint>

                    {/* ── Capacity & status ────────────────────────────────── */}
                    <p style={{ margin: '18px 0 4px', fontWeight: 600 }}>Capacity & status</p>
                    <div style={{ display: 'flex', gap: 16 }}>
                        <MaterialTextField
                            label="Attendee capacity"
                            type="number"
                            inputProps={{ min: 1, step: 1 }}
                            value={form.attendeeCapacity}
                            onChange={(e) => set('attendeeCapacity', e.target.value === '' ? '' : Number(e.target.value))}
                        />
                        <MaterialTextField label="Current attendees" type="number" value={currentAttendeeCount} disabled />
                        <MaterialTextField
                            name="select"
                            select
                            label="Status"
                            value={form.status}
                            onChange={(e) => set('status', e.target.value as CohortStatus)}
                        >
                            <MaterialFormOption value="draft">Draft</MaterialFormOption>
                            <MaterialFormOption value="active">Active</MaterialFormOption>
                            <MaterialFormOption value="completed">Completed</MaterialFormOption>
                            <MaterialFormOption value="archived">Archived</MaterialFormOption>
                        </MaterialTextField>
                    </div>
                    <Hint>Current attendees is maintained automatically and can’t be edited.</Hint>

                    {/* ── Members ──────────────────────────────────────────── */}
                    <p style={{ margin: '18px 0 4px', fontWeight: 600 }}>Members</p>
                    <Hint>
                        Search users by email to add them. Added members get a year of Premium + Course 33 immediately (as if they bought it) and can
                        join this cohort’s live sessions. Anyone without a Harmony Board order also gets one (a $0 order in Orders, to be shipped), so
                        don’t add staff or test accounts. Removing a member revokes session access but keeps their granted access and their board
                        order.
                    </Hint>
                    <CohortMembers
                        cohortId={selectedCohortId ?? createdCohortId ?? undefined}
                        onStagedChange={setStagedMembers}
                        onCountChange={setCurrentAttendeeCount}
                    />

                    {/* ── Live sessions ────────────────────────────────────── */}
                    <p style={{ margin: '18px 0 4px', fontWeight: 600 }}>Live sessions</p>
                    <Hint>
                        The scheduled live calls for this cohort. Enter each time as it should read in the cohort&apos;s timezone
                        {form.timezone ? ` (${form.timezone})` : ''} — attendees see it automatically converted to their own local time. Each also
                        needs a meeting link and a duration.
                    </Hint>
                    {form.liveSessions.map((s, i) => (
                        <div key={i} style={{ border: '1px solid #e0e0e0', borderRadius: 4, padding: 12, marginBottom: 12 }}>
                            <div className="d-flex align-items-center justify-content-between" style={{ marginBottom: 8 }}>
                                <strong style={{ fontSize: 13 }}>Session {i + 1}</strong>
                                <button
                                    type="button"
                                    onClick={() => removeSession(i)}
                                    style={{ border: 'none', background: 'transparent', color: darkDangerText, cursor: 'pointer', fontSize: 13 }}
                                >
                                    Remove
                                </button>
                            </div>
                            <MaterialTextField
                                label="Scheduled at"
                                type="datetime-local"
                                InputLabelProps={{ shrink: true }}
                                value={s.scheduledAt}
                                onChange={(e) => setSession(i, 'scheduledAt', e.target.value)}
                            />
                            {/* The two destinations, in preference order. Our own room
                                keeps members in the app and is the only one that can
                                give them an ending card; the Meet link is the
                                fallback we keep. */}
                            <MaterialTextField
                                name="liveSessionId"
                                select
                                label="Harmonea live session"
                                value={s.liveSessionId}
                                onChange={(e) => setSession(i, 'liveSessionId', e.target.value)}
                                helperText={
                                    s.liveSessionId
                                        ? 'Members join inside the app. The Google Meet link below is ignored.'
                                        : 'Pick one of our own rooms — or leave blank and paste a Meet link below.'
                                }
                            >
                                <MaterialFormOption value="">— none (use a meeting link) —</MaterialFormOption>
                                {(liveSessionsResponse?.data?.sessions ?? []).map((ls) => (
                                    <MaterialFormOption key={ls.id} value={ls.id}>
                                        {ls.title} · {ls.durationMinutes}m · {ls.hostName}
                                    </MaterialFormOption>
                                ))}
                            </MaterialTextField>
                            <MaterialTextField
                                label="Google Meet link (fallback)"
                                type="url"
                                value={s.externalLink}
                                disabled={!!s.liveSessionId}
                                onChange={(e) => setSession(i, 'externalLink', e.target.value)}
                                helperText={
                                    s.liveSessionId
                                        ? 'Not used — this session runs in our own room.'
                                        : s.meetSpaceId
                                          ? 'Auto-created Google Meet link.'
                                          : 'Paste the meeting link for this session (Google Meet, Zoom, …).'
                                }
                            />
                            <div style={{ display: 'flex', gap: 16 }}>
                                <MaterialTextField
                                    label="Duration (minutes)"
                                    type="number"
                                    inputProps={{ min: 1, step: 1 }}
                                    value={s.durationMinutes}
                                    onChange={(e) => setSession(i, 'durationMinutes', e.target.value === '' ? '' : Number(e.target.value))}
                                />
                                <MaterialTextField
                                    label="Day number (0–21)"
                                    type="number"
                                    inputProps={{ min: 0, max: 21, step: 1 }}
                                    value={s.dayNumber}
                                    onChange={(e) => setSession(i, 'dayNumber', e.target.value === '' ? '' : Number(e.target.value))}
                                />
                            </div>
                            <MaterialTextField label="Title (optional)" value={s.title} onChange={(e) => setSession(i, 'title', e.target.value)} />
                            <MaterialTextField
                                label="Recording URL (auto-filled after the session)"
                                value={s.recordingUrl}
                                onChange={(e) => setSession(i, 'recordingUrl', e.target.value)}
                            />
                            {(s.transcriptUrl || s.attendeeCount != null) && (
                                <div style={{ fontSize: 12, color: '#9a9aa2', marginTop: 4 }}>
                                    {s.transcriptUrl && (
                                        <a href={s.transcriptUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#7da3ff' }}>
                                            View transcript
                                        </a>
                                    )}
                                    {s.transcriptUrl && s.attendeeCount != null && ' · '}
                                    {s.attendeeCount != null && `${s.attendeeCount} attendee${s.attendeeCount === 1 ? '' : 's'}`}
                                </div>
                            )}
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={addSession}
                        style={{
                            border: '1px dashed #6b6b73',
                            background: 'transparent',
                            color: '#dddddd',
                            borderRadius: 4,
                            padding: '8px 16px',
                            cursor: 'pointer',
                            fontSize: 13,
                        }}
                    >
                        + Add live session
                    </button>
                </MaterialFormContainer>
            }
            toggle={toggle}
            handleSave={handleSave}
            handleDelete={handleDelete}
            loading={isLoading || isSaving || isCreating || isUpdating || isDeleting}
        />
    );
};

export default CohortModal;
